<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { Plus, Pencil, Trash2 } from 'lucide-svelte';
  import { announcementsApi, type AnnouncementBody } from '$lib/api/announcements';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import { session } from '$lib/state/session.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import { t } from '$lib/i18n';
  import type { Announcement } from '$lib/types';

  const queryClient = useQueryClient();
  const query = createQuery(() => ({
    queryKey: ['announcements'],
    queryFn: () => announcementsApi.list()
  }));

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['announcements'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }

  function fmtDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  let editing = $state<Announcement | null>(null);
  let showForm = $state(false);
  let form = $state<AnnouncementBody>({ title: '', body: '' });
  let saving = $state(false);
  let error = $state('');

  function openCreate() {
    editing = null;
    form = { title: '', body: '' };
    error = '';
    showForm = true;
  }

  function openEdit(a: Announcement) {
    editing = a;
    form = { title: a.title, body: a.body };
    error = '';
    showForm = true;
  }

  async function save() {
    if (!form.title.trim() || !form.body.trim()) {
      error = 'Title and body are required.';
      return;
    }
    saving = true;
    error = '';
    try {
      if (editing) {
        await announcementsApi.update(editing.id, form);
        toast.success('Announcement updated.');
      } else {
        await announcementsApi.create(form);
        toast.success('Announcement posted.');
      }
      refresh();
      showForm = false;
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      saving = false;
    }
  }

  async function remove(id: number) {
    if (!confirm('Delete this announcement?')) return;
    try {
      await announcementsApi.remove(id);
      refresh();
      toast.success('Announcement deleted.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }
</script>

<div class="tym-page">
  <div class="mb-4 flex items-center justify-between">
    <p class="tym-section-sub">{t('announcements.subtitle')}</p>
    {#if session.isHROrAdmin}
      <button type="button" class="btn-primary" onclick={openCreate}>
        <Plus class="h-4 w-4" /> New announcement
      </button>
    {/if}
  </div>

  {#if query.isLoading}
    <div class="tym-loading-state">{t('common.loading')}</div>
  {:else if query.isError}
    <div class="tym-error-state">{t('announcements.error')}</div>
  {:else if query.data}
    <div class="space-y-3">
      {#each query.data as a (a.id)}
        <div class="tym-card p-5">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="font-heading text-base font-bold text-navy">{a.title}</p>
              <p class="tym-section-sub mb-2">{a.author_name || t('announcements.authorFallback')} &middot; {fmtDate(a.created_at)}</p>
            </div>
            {#if session.isHROrAdmin}
              <div class="flex shrink-0 gap-1.5">
                <button type="button" class="btn-secondary !px-2" onclick={() => openEdit(a)} aria-label="Edit">
                  <Pencil class="h-3.5 w-3.5" />
                </button>
                <button type="button" class="btn-danger !px-2" onclick={() => remove(a.id)} aria-label="Delete">
                  <Trash2 class="h-3.5 w-3.5" />
                </button>
              </div>
            {/if}
          </div>
          <p class="whitespace-pre-wrap text-[13.5px] text-toyama-heavy-carbon">{a.body}</p>
        </div>
      {:else}
        <div class="tym-empty-state">{t('announcements.empty')}</div>
      {/each}
    </div>
  {/if}

  {#if showForm}
    <Modal title={editing ? 'Edit announcement' : 'New announcement'} onClose={() => (showForm = false)}>
      <div class="space-y-4">
        <div>
          <div class="label">Title</div>
          <input class="input" bind:value={form.title} />
        </div>
        <div>
          <div class="label">Body</div>
          <textarea class="input" rows="6" bind:value={form.body}></textarea>
        </div>
        {#if error}<div class="tym-error-state">{error}</div>{/if}
        <div class="flex justify-end gap-2 pt-1">
          <button type="button" class="btn-secondary" onclick={() => (showForm = false)}>Cancel</button>
          <button type="button" class="btn-primary" disabled={saving} onclick={save}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  {/if}
</div>
